import { FormEvent, useState } from 'react'
import { Alerta, Boton, Campo, Dialogo, Entrada, Seleccion } from '../kit'
import { useMarcarNoUbicados, useRecintos, useTrasladar } from '../../queries/useInventario'
import { Bien } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { SECTOR } from '../../utils/inventario'

/** El «destino» que no es una oficina: el bien no se encontró. */
const NO_UBICADO = '__no_ubicado__'

/**
 * Llevar uno o varios bienes a otra oficina: las dos planchetas quedan con
 * modificaciones. El primer destino de la lista es «Marcar como no ubicado», para cuando
 * el bien no está en ninguna parte; entonces el motivo es obligatorio.
 */
export function DialogoTraslado({ bienes, onCerrar, onHecho }: { bienes: Bien[]; onCerrar: () => void; onHecho?: () => void }) {
  const recintos = useRecintos()
  const trasladar = useTrasladar()
  const noUbicados = useMarcarNoUbicados()
  const [destino, setDestino] = useState('')
  const [motivo, setMotivo] = useState('')
  const origenes = new Set(bienes.map((b) => b.recinto?.codigo))
  const opciones = (recintos.data ?? []).filter((r) => r.activo && !(origenes.size === 1 && origenes.has(r.codigo)))
  const aNoUbicado = destino === NO_UBICADO
  const accion = aNoUbicado ? noUbicados : trasladar

  const guardar = (e: FormEvent) => {
    e.preventDefault()
    const listo = {
      onSuccess: () => {
        onHecho?.()
        onCerrar()
      },
    }
    const ids = bienes.map((b) => b.id)
    if (aNoUbicado) noUbicados.mutate({ bienes: ids, motivo }, listo)
    else trasladar.mutate({ bienes: ids, destino, motivo }, listo)
  }

  const titulo = bienes.length === 1 ? `Trasladar ${bienes[0].numero || bienes[0].descripcion}` : `Trasladar ${bienes.length} bienes`
  return (
    <Dialogo abierto onCerrar={onCerrar} titulo={titulo}>
      <form className="pila" onSubmit={guardar}>
        {bienes.length > 1 && (
          <p className="texto-secundario">
            {bienes.slice(0, 4).map((b) => b.numero || b.descripcion).join(', ')}
            {bienes.length > 4 && ` y ${bienes.length - 4} más`}
          </p>
        )}
        <Campo etiqueta="Oficina de destino" requerido>
          <Seleccion value={destino} onChange={(e) => setDestino(e.target.value)} required>
            <option value="">{recintos.isLoading ? 'Cargando…' : 'Elige la oficina'}</option>
            <option value={NO_UBICADO}>Marcar como no ubicado</option>
            {opciones.map((r) => (
              <option key={r.codigo} value={r.codigo}>
                {r.nombre} · {SECTOR[r.sector]}{r.piso != null ? ` ${r.piso}` : ''} ({r.codigo})
              </option>
            ))}
          </Seleccion>
        </Campo>
        {aNoUbicado ? (
          <Campo etiqueta="Motivo" requerido ayuda="Dónde se buscó o qué se sabe. Quedan en su oficina como no ubicados.">
            <Entrada value={motivo} onChange={(e) => setMotivo(e.target.value)} required />
          </Campo>
        ) : (
          <Campo etiqueta="Motivo" ayuda="Opcional. Queda en el historial de las dos oficinas.">
            <Entrada value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </Campo>
        )}
        {accion.isError && <Alerta tipo="error">{mensajeDeError(accion.error)}</Alerta>}
        <div className="acciones-formulario">
          <Boton variante="text" onClick={onCerrar}>Cancelar</Boton>
          {aNoUbicado ? (
            <Boton type="submit" color="error" cargando={noUbicados.isPending} disabled={!motivo.trim()}>Marcar no ubicado</Boton>
          ) : (
            <Boton type="submit" cargando={trasladar.isPending} disabled={!destino}>Trasladar</Boton>
          )}
        </div>
      </form>
    </Dialogo>
  )
}
